import React, { useState } from 'react';
import { api } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Loader2, Database } from 'lucide-react';
import { toast } from 'sonner';

const FEDERAL_2024_DATA = [
  {
    tax_year: 2024,
    jurisdiction: 'federal',
    filing_status: 'single',
    standard_deduction: 14600,
    tax_brackets: [
      { min_income: 0, max_income: 11600, rate: 0.10, base_tax: 0 },
      { min_income: 11600, max_income: 47150, rate: 0.12, base_tax: 1160 },
      { min_income: 47150, max_income: 100525, rate: 0.22, base_tax: 5426 },
      { min_income: 100525, max_income: 191950, rate: 0.24, base_tax: 17168.50 },
      { min_income: 191950, max_income: 243725, rate: 0.32, base_tax: 39110.50 },
      { min_income: 243725, max_income: 609350, rate: 0.35, base_tax: 55678.50 },
      { min_income: 609350, max_income: 999999999, rate: 0.37, base_tax: 183647.25 }
    ],
    notes: '2024 Federal Tax Rates - Single Filers'
  },
  {
    tax_year: 2024,
    jurisdiction: 'federal',
    filing_status: 'married',
    standard_deduction: 29200,
    tax_brackets: [
      { min_income: 0, max_income: 23200, rate: 0.10, base_tax: 0 },
      { min_income: 23200, max_income: 94300, rate: 0.12, base_tax: 2320 },
      { min_income: 94300, max_income: 201050, rate: 0.22, base_tax: 10852 },
      { min_income: 201050, max_income: 383900, rate: 0.24, base_tax: 34337 },
      { min_income: 383900, max_income: 487450, rate: 0.32, base_tax: 78221 },
      { min_income: 487450, max_income: 731200, rate: 0.35, base_tax: 111357 },
      { min_income: 731200, max_income: 999999999, rate: 0.37, base_tax: 196669.50 }
    ],
    notes: '2024 Federal Tax Rates - Married Filing Jointly'
  },
  {
    tax_year: 2024,
    jurisdiction: 'federal',
    filing_status: 'head_of_household',
    standard_deduction: 21900,
    tax_brackets: [
      { min_income: 0, max_income: 16550, rate: 0.10, base_tax: 0 },
      { min_income: 16550, max_income: 63100, rate: 0.12, base_tax: 1655 },
      { min_income: 63100, max_income: 100500, rate: 0.22, base_tax: 7241 },
      { min_income: 100500, max_income: 191950, rate: 0.24, base_tax: 15469 },
      { min_income: 191950, max_income: 243700, rate: 0.32, base_tax: 37417 },
      { min_income: 243700, max_income: 609350, rate: 0.35, base_tax: 53977 },
      { min_income: 609350, max_income: 999999999, rate: 0.37, base_tax: 181954.50 }
    ],
    notes: '2024 Federal Tax Rates - Head of Household'
  }
];

export default function TaxRateSeedData({ onComplete }) {
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(false);

  const seedData = async () => {
    setSeeding(true);
    try {
      for (const config of FEDERAL_2024_DATA) {
        await api.entities.TaxRateConfig.create(config);
      }
      toast.success('Successfully seeded 2024 federal tax rates');
      setSeeded(true);
      if (onComplete) onComplete();
    } catch (err) {
      console.error(err);
      toast.error('Failed to seed tax rates');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-500" />
          Seed 2024 Federal Tax Rates
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Quick-load 2024 federal tax brackets for Single, Married, and Head of Household filing statuses.
        </p>
        
        {seeded ? (
          <div className="flex items-center gap-2 text-emerald-600">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Tax rates seeded successfully!</span>
          </div>
        ) : (
          <Button 
            onClick={seedData} 
            disabled={seeding}
            className="bg-blue-500 hover:bg-blue-600"
          >
            {seeding ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Seeding...
              </>
            ) : (
              <>
                <Database className="w-4 h-4 mr-2" />
                Seed 2024 Federal Rates
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}